/*
 * color-mixer-recipe.js — the «find a paint recipe» tab of /color-mixer.
 *
 * Four steps, each answering one question a person with a real palette has:
 *   1. WHICH COLOUR — typed (hex / CSS / RAL / a colour name in the page's
 *      language) or taken from a photo: loupe, averaging window, and a white
 *      reference that fixes both the camera's colour cast and its exposure.
 *   2. WHICH PAINTS — the person's own set: pigments from the calibrated
 *      library (color-mixer-paint.js) and/or their own paints by colour or
 *      from a photo of swatches. Recipes use ONLY these.
 *   3. THE RECIPE — up to 4 paints, in whole parts when parts make sense, in
 *      ml / drops for any batch size, with ΔE2000 in words; when the set
 *      cannot reach the colour, which one tube would.
 *   4. FIX THE BATCH — the person mixed it and it came out different: the
 *      miss is explained by the strength of their paints, and the answer is
 *      what to ADD (paint cannot be taken back out). The learned strengths
 *      can be kept for the next recipes.
 *
 * The search runs in color-mixer-solver-worker.js (≈0.1 s on a desktop for
 * 27 paints × 4, several times that on a phone); the same engine runs here
 * as a fallback when a Worker cannot start.
 */
(function () {
    'use strict';
    var root = document.getElementById('cmr');
    if (!root || root.__cmrInit) return;
    root.__cmrInit = true;

    var W = window;
    var P = W.CMXPaint, CMX = W.CMX;
    var CFG = W.__CMR_CFG || {};
    var L = CFG.lang || {};
    var PIGN = W.__CMX_PIGMENTS || {};
    var LN = W.__CMX_LANG || {};
    var $ = function (id) { return document.getElementById(id); };
    var t = function (k, d) { return L[k] != null && L[k] !== '' ? L[k] : d; };
    var fmt = function (s, o) { return String(s).replace(/:(\w+)/g, function (m, k) { return o[k] != null ? o[k] : m; }); };
    var toast = function (m, ty) { try { (W.__toast || function () {})(m, ty || 'success'); } catch (e) {} };
    /* One event per action, and at most 12 of a kind per page view: the old
       reverse block sent up to 153 «reverse_solve» per session (one per step
       of a dragged colour input), which drowned the count of people. */
    var aeSeen = {};
    var ae = function (f, data) {
        aeSeen[f] = (aeSeen[f] || 0) + 1;
        if (aeSeen[f] > 12) return;
        try { (W.__ae || function () {})('feature_use', Object.assign({ slug: 'color-mixer', f: f }, data || {})); } catch (e) {}
    };
    var settings = W.__toolSettings;
    var locale = CFG.locale || document.documentElement.lang || 'en';
    var num = function (x, d) {
        if (d === 0) x = Math.round(x);
        try { return new Intl.NumberFormat(locale, { maximumFractionDigits: d == null ? 1 : d }).format(x); } catch (e) { return String(Math.round(x * 10) / 10); }
    };
    var PR = (function () { try { return new Intl.PluralRules(locale); } catch (e) { return null; } })();
    /* The plural form must follow the number the person READS: 28.8 shown as
       «29» is «29 частей», not the fractional «части». */
    function plural(key, n, digits) {
        var forms = L[key] || {};
        var d = digits == null ? 1 : digits;
        var p = Math.pow(10, d), shownN = Math.round(n * p) / p;
        var cat = PR ? PR.select(shownN) : (shownN === 1 ? 'one' : 'other');
        var f = forms[cat] || forms.other || forms.one || ':n';
        return f.replace(':n', num(shownN, d));
    }
    if (!P || !CMX) return;

    function el(tag, attrs, kids) {
        var n = document.createElement(tag);
        if (attrs) for (var k in attrs) {
            var v = attrs[k];
            if (v == null) continue;
            if (k === 'class') n.className = v;
            else if (k === 'text') n.textContent = v;
            else if (k.slice(0, 2) === 'on' && typeof v === 'function') n.addEventListener(k.slice(2), v);
            else n.setAttribute(k, v);
        }
        if (kids) (Array.isArray(kids) ? kids : [kids]).forEach(function (c) { if (c != null) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
        return n;
    }
    function hexOf(rgb) { return P.hexOfRgb(rgb); }
    function rgbOfHex(h) { var c = CMX.parseHex(h); return c ? CMX.to255(c) : null; }

    /* ================================================================ paints == */
    var PRESETS = {
        basic: ['titanium_white', 'carbon_black', 'hansa_yellow', 'pyrrole_red', 'ultramarine_blue'],
        artist: ['titanium_white', 'carbon_black', 'hansa_yellow', 'yellow_ochre', 'burnt_sienna', 'burnt_umber',
            'pyrrole_red', 'quinacridone_crimson', 'ultramarine_blue', 'phthalo_blue', 'phthalo_green', 'dioxazine_purple'],
        all: P.libraryIds()
    };
    var STRENGTH = { weak: 0.6, normal: 1, strong: 1.6, very: 2.6 };
    var WHITE = P.pigment('titanium_white');

    var saved = (settings && settings.load('color-mixer', 'palette', null)) || null;
    var pal = {
        set: (saved && saved.set) || 'artist',
        on: {},
        custom: (saved && Array.isArray(saved.custom)) ? saved.custom.filter(function (c) { return c && /^#[0-9a-f]{6}$/i.test(c.hex); }) : [],
        learned: (saved && saved.learned && typeof saved.learned === 'object') ? saved.learned : {},
        max: (saved && saved.max >= 1 && saved.max <= 4) ? saved.max : 3,
        amount: (saved && saved.amount > 0) ? saved.amount : 100,
        unit: (saved && (saved.unit === 'g' || saved.unit === 'ml')) ? saved.unit : 'ml',
        // real paints chosen by manufacturer, line and number: [{ l: lineId, c: code, on }]
        brand: (saved && Array.isArray(saved.brand)) ? saved.brand.filter(function (b) { return b && typeof b.l === 'string' && typeof b.c === 'string'; }) : []
    };
    if (saved && saved.on && typeof saved.on === 'object') pal.on = saved.on;
    else PRESETS.artist.forEach(function (id) { pal.on[id] = true; });
    var customSeq = pal.custom.reduce(function (m, c) { return Math.max(m, +String(c.uid).replace(/\D/g, '') || 0); }, 0);

    function savePal() {
        try { settings && settings.save('color-mixer', 'palette', { set: pal.set, on: pal.on, custom: pal.custom, learned: pal.learned, max: pal.max, amount: pal.amount, unit: pal.unit, brand: pal.brand }); } catch (e) {}
    }
    /* Chips on screen: what is enabled plus what was switched off in this
       visit — a chip must not vanish under the finger that just unticked it. */
    var shown = {};
    Object.keys(pal.on).forEach(function (id) { if (pal.on[id]) shown[id] = true; });
    function applyPreset(set) {
        pal.set = set;
        if (PRESETS[set]) { pal.on = {}; shown = {}; PRESETS[set].forEach(function (id) { pal.on[id] = true; shown[id] = true; }); }
        savePal();
    }
    function pigName(id) { return PIGN[id] || id.replace(/_/g, ' '); }

    /* ---- paints by brand ------------------------------------------------------
       public/js/color-mixer-brands.js, generated by scripts/color-mixer/brands/
       build-catalog.cjs: real paints — manufacturer, line, colour number — as
       mixes of the library pigments, built from the pigments the manufacturer
       publishes (proportions of multi-pigment paints fitted to its own swatch).
       Loaded only when the picker opens or the palette already holds such paints. */
    var brandsLoading = null, BRAND_IDX = null;
    function ensureBrands() {
        if (BRAND_IDX) return Promise.resolve(W.CMXBrands);
        if (!brandsLoading) {
            brandsLoading = loadScript(Array.isArray(CFG.brands) ? CFG.brands : (CFG.brands ? [CFG.brands] : [])).then(function () {
                if (!W.CMXBrands || !W.CMXBrands.lines) throw new Error('brands');
                BRAND_IDX = {};
                W.CMXBrands.lines.forEach(function (L) {
                    var by = {};
                    L.colors.forEach(function (c) { by[c[0]] = c; });
                    BRAND_IDX[L.id] = { L: L, by: by };
                });
                return W.CMXBrands;
            });
            brandsLoading.catch(function () { brandsLoading = null; ae('brands_load_failed', {}); });
        }
        return brandsLoading;
    }
    /* catalogue row: [code, name, mix [[libId, share]], strength, quality e|m|a|r, fit ΔE, pigments] */
    function brandEntry(b) {
        var x = BRAND_IDX && BRAND_IDX[b.l], c = x && x.by[b.c];
        return c ? { L: x.L, c: c } : null;
    }
    var _brandPrep = {};
    function prepBrand(b) {
        var e = brandEntry(b); if (!e) return null;
        var key = b.l + '|' + b.c;
        if (!_brandPrep[key]) _brandPrep[key] = P.composite(e.c[2], e.c[3], {});
        return _brandPrep[key];
    }
    var INTL = !/^(ru|uk)$/.test(locale);
    function brandLabel(L) { return INTL ? (L.brandEn || L.brand) : L.brand; }
    function lineLabel(L) { return INTL ? (L.lineEn || L.line) : L.line; }
    function isApprox(c) { return c[4] === 'a' || c[4] === 'r'; }
    /* the manufacturer's English name where it publishes one (Гамма prints
       both), else the name on the tube */
    function tubeName(c) { return INTL && c[7] ? c[7] : c[1]; }
    /* the small caption under a paint's name, for every kind of paint */
    function captionOf(desc) {
        if (desc.kind === 'lib') return P.LIB[desc.id].ci;
        if (desc.kind === 'brand') {
            var e = brandEntry(desc.brand);
            return e ? lineLabel(e.L) + ' №' + e.c[0] + (e.c[6] ? ' · ' + e.c[6] : '') + (isApprox(e.c) ? ' · ≈' : '') : '';
        }
        return t('rcp_custom_badge', '');
    }

    /* Prepared paints the solver sees, in a stable order: enabled library
       pigments, then enabled custom paints. Each carries a descriptor so the
       result can be shown and re-created (share link, mixer). */
    var _customPrep = {};
    function prepCustom(c) {
        var key = c.hex + '|' + c.strength;
        if (!_customPrep[key]) {
            var rgb = rgbOfHex(c.hex);
            var R = W.spectral ? new W.spectral.Color(rgb).R : null;
            if (!R) return null;
            _customPrep[key] = P.prepare(R, STRENGTH[c.strength] || 1, {});
        }
        return _customPrep[key];
    }
    function activePaints() {
        var out = [];
        P.libraryIds().forEach(function (id) {
            if (!pal.on[id]) return;
            var p = P.pigment(id); if (!p) return;
            var s = pal.learned[id];
            out.push({ key: id, kind: 'lib', id: id, name: pigName(id), paint: s ? P.adjust(p, s) : p, learned: s || null });
        });
        pal.custom.forEach(function (c) {
            if (c.on === false) return;
            var p = prepCustom(c); if (!p) return;
            var s = pal.learned['c:' + c.uid];
            out.push({ key: 'c:' + c.uid, kind: 'custom', custom: c, name: c.name, paint: s ? P.adjust(p, s) : p, learned: s || null });
        });
        pal.brand.forEach(function (b) {
            if (b.on === false) return;
            var p = prepBrand(b); if (!p) return;       // catalogue not loaded yet: ensureBrands() re-solves
            var key = 'b:' + b.l + ':' + b.c, s = pal.learned[key], e = brandEntry(b);
            out.push({ key: key, kind: 'brand', brand: b, name: tubeName(e.c), paint: s ? P.adjust(p, s) : p, learned: s || null, approx: isApprox(e.c) });
        });
        return out;
    }
    function swatchOf(desc) {
        var mass, tint;
        if (desc.kind === 'lib' || desc.kind === 'brand') {
            var p = desc.kind === 'lib' ? P.pigment(desc.id) : prepBrand(desc.brand);
            mass = p ? hexOf(P.paintRgb(p)) : '#9ca3af'; tint = p ? hexOf(P.tintRgb(p, WHITE, 0.2)) : mass;
        } else {
            var q = prepCustom(desc.custom);
            mass = desc.custom.hex; tint = q ? hexOf(P.tintRgb(q, WHITE, 0.2)) : mass;
        }
        return el('span', { class: 'cmr-psw', 'aria-hidden': 'true' }, [el('i', { style: 'background:' + mass }), el('i', { style: 'background:' + tint })]);
    }

    function renderPaints() {
        var box = $('cmr-paints'); if (!box) return;
        box.innerHTML = '';
        var n = 0;
        P.libraryIds().forEach(function (id) {
            if (!pal.on[id] && !shown[id]) return;
            var on = !!pal.on[id];
            if (on) n++;
            var desc = { kind: 'lib', id: id };
            var learned = pal.learned[id];
            var chip = el('button', {
                type: 'button', class: 'cmr-paint', 'aria-pressed': on ? 'true' : 'false',
                title: fmt(t('rcp_toggle', ':name'), { name: pigName(id) }),
                onclick: function () { pal.on[id] = !pal.on[id]; pal.set = 'mine'; syncSet(); savePal(); renderPaints(); renderLib(); schedule(); ae('paint_toggle', { id: id, on: pal.on[id] ? 1 : 0 }); }
            }, [swatchOf(desc), el('span', { class: 'cmr-pname' }, [pigName(id), el('small', { text: P.LIB[id].ci + (learned ? ' · ' + fmt(t('rcp_learned_badge', '×:x'), { x: num(learned, 1) }) : '') })]), el('span', { class: 'cmr-pcheck', text: '✓' })]);
            box.appendChild(chip);
        });
        pal.custom.forEach(function (c) {
            var on = c.on !== false;
            if (on) n++;
            var learned = pal.learned['c:' + c.uid];
            var wrap = el('div', { class: 'cmr-paint', 'data-on': on ? 'true' : 'false', role: 'group' });
            var toggle = el('button', {
                type: 'button', class: 'cmr-ptog', 'aria-pressed': on ? 'true' : 'false',
                title: fmt(t('rcp_toggle', ':name'), { name: c.name }),
                onclick: function () { c.on = !on; pal.set = 'mine'; syncSet(); savePal(); renderPaints(); schedule(); }
            }, [swatchOf({ kind: 'custom', custom: c }), el('span', { class: 'cmr-pname' }, [c.name, el('small', { text: t('rcp_custom_badge', 'mine') + ' · ' + c.hex + (learned ? ' · ' + fmt(t('rcp_learned_badge', '×:x'), { x: num(learned, 1) }) : '') })])]);
            var rm = el('button', {
                type: 'button', class: 'cmr-prm', text: '✕', 'aria-label': fmt(t('rcp_remove', 'Remove :name'), { name: c.name }),
                onclick: function () { pal.custom = pal.custom.filter(function (x) { return x !== c; }); delete pal.learned['c:' + c.uid]; savePal(); renderPaints(); schedule(); }
            });
            wrap.appendChild(toggle); wrap.appendChild(rm);
            box.appendChild(wrap);
        });
        pal.brand.forEach(function (b) {
            var e = brandEntry(b);
            var on = b.on !== false;
            if (on && e) n++;
            var key = 'b:' + b.l + ':' + b.c, learned = pal.learned[key];
            var name = e ? tubeName(e.c) : '№' + b.c;
            var wrap = el('div', { class: 'cmr-paint', 'data-on': on ? 'true' : 'false', role: 'group' });
            var toggle = el('button', {
                type: 'button', class: 'cmr-ptog', 'aria-pressed': on ? 'true' : 'false',
                title: fmt(t('rcp_toggle', ':name'), { name: name }),
                onclick: function () { b.on = !on; pal.set = 'mine'; syncSet(); savePal(); renderPaints(); schedule(); }
            }, [swatchOf({ kind: 'brand', brand: b }), el('span', { class: 'cmr-pname' }, [name, el('small', { text: captionOf({ kind: 'brand', brand: b }) + (learned ? ' · ' + fmt(t('rcp_learned_badge', '×:x'), { x: num(learned, 1) }) : '') })])]);
            var rm = el('button', {
                type: 'button', class: 'cmr-prm', text: '✕', 'aria-label': fmt(t('rcp_remove', 'Remove :name'), { name: name }),
                onclick: function () { removeBrand(b); }
            });
            wrap.appendChild(toggle); wrap.appendChild(rm);
            box.appendChild(wrap);
        });
        var cnt = $('cmr-paints-count'); if (cnt) cnt.textContent = '(' + n + ')';
    }
    function syncSet() {
        var sel = $('cmr-set'); if (sel) sel.value = pal.set;
    }
    function renderLib() {
        var box = $('cmr-add-lib'); if (!box) return;
        box.innerHTML = '';
        var any = false;
        P.libraryIds().forEach(function (id) {
            if (pal.on[id]) return;
            any = true;
            box.appendChild(el('button', {
                type: 'button', class: 'cmr-paint', 'aria-pressed': 'false', title: pigName(id),
                onclick: function () { pal.on[id] = true; shown[id] = true; pal.set = 'mine'; syncSet(); savePal(); renderPaints(); renderLib(); schedule(); ae('paint_add', { src: 'lib', id: id }); }
            }, [swatchOf({ kind: 'lib', id: id }), el('span', { class: 'cmr-pname' }, [pigName(id), el('small', { text: P.LIB[id].ci })]), el('span', { class: 'cmr-pcheck', style: 'visibility:visible', text: '＋' })]));
        });
        if (!any) box.appendChild(el('p', { class: 'cmr-note', text: t('rcp_add_lib_empty', '') }));
    }
    function addCustom(hex, name, strength, src) {
        customSeq++;
        var c = { uid: 'u' + customSeq, hex: hex.toLowerCase(), name: (name || '').trim() || fmt(t('ph_paint_default', 'Paint :n'), { n: customSeq }), strength: STRENGTH[strength] ? strength : 'normal', on: true };
        pal.custom.push(c);
        pal.set = 'mine'; syncSet();
        savePal(); renderPaints(); schedule();
        ae('paint_add', { src: src || 'color' });
        return c;
    }

    /* ============================================================== target == */
    var target = null; // { rgb, lab, src, raw? }
    var ralLoading = null;
    function loadScript(urls) {
        return new Promise(function (resolve, reject) {
            var i = 0;
            (function next() {
                if (i >= urls.length) { reject(new Error('load')); return; }
                var s = document.createElement('script');
                var nonce = (document.querySelector('script[nonce]') || {}).nonce;
                if (nonce) s.nonce = nonce;
                s.src = urls[i++]; s.async = true;
                s.onload = function () { resolve(); };
                s.onerror = function () { s.remove(); next(); };
                document.head.appendChild(s);
            })();
        });
    }
    function ensureRal() {
        if (W.ColorPickData) return Promise.resolve(W.ColorPickData);
        if (!ralLoading) ralLoading = loadScript(CFG.ral || []).then(function () { return W.ColorPickData; });
        return ralLoading;
    }
    var nameMap = null;
    function localNames() {
        if (nameMap) return nameMap;
        nameMap = {};
        Object.keys(LN).forEach(function (k) {
            if (k.indexOf('name_') !== 0) return;
            var c = CMX.namedColor(k.slice(5));
            if (c) nameMap[String(LN[k]).toLowerCase().replace(/ё/g, 'е').trim()] = c;
        });
        return nameMap;
    }
    /* Resolves to {rgb, label} or null. */
    function parseTarget(text) {
        var s = String(text || '').trim();
        if (!s) return Promise.resolve(null);
        var m = s.match(/^ral\s*(\d{4})$/i) || s.match(/^(\d{4})$/);
        if (m) {
            return ensureRal().then(function (D) {
                var row = D && D.ROWS && D.ROWS.filter(function (r) { return r[0] === 'RAL ' + m[1]; })[0];
                if (!row) { toast(t('rcp_ral_unknown', 'Unknown RAL'), 'warning'); return null; }
                var name = D.name ? D.name(row, locale) : row[2];
                return { rgb: rgbOfHex(row[1]), label: row[0] + (name ? ' · ' + name : '') };
            }, function () { return null; });
        }
        var c = CMX.parse(s);
        if (!c) {
            var nm = localNames()[s.toLowerCase().replace(/ё/g, 'е').replace(/[«»"„“”‘’']/g, '').trim()];
            if (nm) c = nm;
        }
        return Promise.resolve(c ? { rgb: CMX.to255(c), label: null } : null);
    }
    function setTarget(rgb, src, label, raw) {
        target = { rgb: rgb.map(function (v) { return Math.max(0, Math.min(255, Math.round(v))); }), src: src, label: label || null, raw: raw || null };
        target.lab = P.labOfRgb(target.rgb);
        obs = null;
        var co = $('cmr-correct-out'); if (co) co.innerHTML = '';
        var hx = hexOf(target.rgb);
        var sw = $('cmr-target-sw'), pick = $('cmr-target-pick'), inp = $('cmr-target-in');
        if (sw) sw.style.background = hx;
        if (pick) pick.value = hx;
        if (inp && src !== 'text') inp.value = hx;
        renderTargetInfo();
        writeHash();
        correction = null;
        schedule(0);
        ae('target', { src: src });
    }
    function renderTargetInfo() {
        var box = $('cmr-target-info'); if (!box) return;
        box.innerHTML = '';
        if (!target) return;
        var hx = hexOf(target.rgb);
        var nm = CMX.nearestName({ r: target.rgb[0] / 255, g: target.rgb[1] / 255, b: target.rgb[2] / 255 });
        var name = nm && LN['name_' + nm.name] ? (nm.exact ? '' : '≈ ') + LN['name_' + nm.name] : '';
        var src = target.src === 'photo' ? t('rcp_src_photo', '') : target.src === 'mixer' ? t('rcp_src_mixer', '') : target.src === 'link' ? t('rcp_src_link', '') : '';
        box.appendChild(el('span', { class: 'cmr-psw', style: 'width:24px;height:24px;flex:0 0 24px;background:' + hx }));
        box.appendChild(el('b', { text: hx }));
        box.appendChild(el('span', { text: [target.label || name, src].filter(Boolean).join(' · ') }));
        if (target.raw && gains) box.appendChild(el('span', { class: 'cmr-muted', text: fmt(t('ph_raw', ''), { hex: hexOf(target.raw) }) }));
        /* The recipe sits below the photo and the paints — on a phone that is two
           screens away. A button, not an automatic jump: the person decides. */
        if (result && result.target === target) box.appendChild(el('button', {
            type: 'button', class: 'cmx-btn cmr-jump', text: t('rcp_jump', '↓'),
            onclick: function () { var r = $('cmr-result-block'); if (r) r.scrollIntoView({ block: 'nearest' }); ae('recipe_jump', {}); }
        }));
    }

    /* ============================================================== photo ==== */
    var photo = { has: false, purpose: 'target', area: 11, wbMode: false };
    var src = document.createElement('canvas');
    var sctx = src.getContext('2d', { willReadFrequently: true });
    var view = $('cmr-canvas'), vctx = view ? view.getContext('2d') : null;
    var loupe = $('cmr-loupe'), lcv = $('cmr-loupe-canvas'), lctx = lcv ? lcv.getContext('2d') : null;
    var gains = null, picks = [], lastPt = null;
    var LOUPE_WIN = 21;

    function ensurePickCore() {
        if (W.ColorPickCore) return Promise.resolve(W.ColorPickCore);
        return loadScript(CFG.pickCore || []).then(function () { return W.ColorPickCore; });
    }
    function loadImageFile(file) {
        if (!file || !/^image\//.test(file.type || 'image/')) { toast(t('ph_err_image', 'Could not open this image'), 'error'); return; }
        var done = function (img, w, h) {
            // ~6 MP is plenty to read a colour and keeps getImageData cheap on phones
            var sc = Math.min(1, Math.sqrt(6e6 / (w * h)));
            src.width = Math.max(1, Math.round(w * sc)); src.height = Math.max(1, Math.round(h * sc));
            sctx.drawImage(img, 0, 0, src.width, src.height);
            if (img.close) try { img.close(); } catch (e) {}
            photo.has = true; gains = null; picks = []; lastPt = null;
            var box = $('cmr-photo'); if (box) box.hidden = false;
            setWbMode(false); renderWb();
            drawView();
            ensurePickCore().catch(function () {});
            ae('photo_loaded', { mp: Math.round(w * h / 1e5) / 10 });
            try { if (W.__stage) W.__stage('photo_loaded'); } catch (e) {}
        };
        if (typeof createImageBitmap === 'function') {
            createImageBitmap(file, { imageOrientation: 'from-image' }).then(function (bmp) { done(bmp, bmp.width, bmp.height); }, fallback);
        } else fallback();
        function fallback() {
            var url = URL.createObjectURL(file), img = new Image();
            img.onload = function () { URL.revokeObjectURL(url); done(img, img.naturalWidth, img.naturalHeight); };
            img.onerror = function () { URL.revokeObjectURL(url); toast(t('ph_err_image', 'Could not open this image'), 'error'); };
            img.src = url;
        }
    }
    function drawView() {
        if (!photo.has || !view) return;
        var maxW = Math.min(860, (view.parentNode && view.parentNode.clientWidth) || 860);
        var sc = Math.min(1, maxW / src.width);
        view.width = Math.max(1, Math.round(src.width * sc));
        view.height = Math.max(1, Math.round(src.height * sc));
        vctx.drawImage(src, 0, 0, view.width, view.height);
        picks.forEach(function (p) { marker(p.x * sc, p.y * sc, p.kind); });
    }
    function marker(x, y, kind) {
        var col = kind === 'white' ? '#ffffff' : kind === 'paint' ? '#f59e0b' : kind === 'mix' ? '#a855f7' : '#38bdf8';
        vctx.lineWidth = 3; vctx.strokeStyle = 'rgba(0,0,0,.7)';
        vctx.beginPath(); vctx.arc(x, y, 11, 0, 7); vctx.stroke();
        vctx.lineWidth = 2; vctx.strokeStyle = col;
        vctx.beginPath(); vctx.arc(x, y, 11, 0, 7); vctx.stroke();
    }
    function toSource(cx, cy) {
        var r = view.getBoundingClientRect();
        var sx = Math.round((cx - r.left) * src.width / r.width), sy = Math.round((cy - r.top) * src.height / r.height);
        return { x: Math.max(0, Math.min(src.width - 1, sx)), y: Math.max(0, Math.min(src.height - 1, sy)) };
    }
    function sampleAt(x, y, size) {
        size = size || photo.area;
        var h = Math.floor(size / 2), x0 = Math.max(0, x - h), y0 = Math.max(0, y - h);
        var w = Math.min(size, src.width - x0), hh = Math.min(size, src.height - y0);
        if (w <= 0 || hh <= 0) return [0, 0, 0];
        var d = sctx.getImageData(x0, y0, w, hh).data, lr = 0, lg = 0, lb = 0, n = d.length / 4;
        // average in LINEAR light — a gamma-space mean of a textured patch is darker than the patch
        for (var i = 0; i < d.length; i += 4) { lr += CMX.s2l(d[i] / 255); lg += CMX.s2l(d[i + 1] / 255); lb += CMX.s2l(d[i + 2] / 255); }
        return [lr / n, lg / n, lb / n].map(function (v) { return Math.round(CMX.l2s(v) * 255); });
    }
    /* White reference: chromatic gains from color-pick-core (with its honest
       refusal), plus exposure — the sheet becomes paper white (Y ≈ 0.85), so
       an under- or over-exposed shot does not change the recipe's lightness. */
    var PAPER_Y = 0.85;
    function makeGains(rgb) {
        var C = W.ColorPickCore; if (!C) return null;
        var g = C.whiteBalanceGains(rgb);
        if (!g) return null;
        var lin = rgb.map(function (v) { return CMX.s2l(v / 255); });
        var y = 0.2126729 * lin[0] * g[0] + 0.7151522 * lin[1] * g[1] + 0.0721750 * lin[2] * g[2];
        if (!(y > 0.04)) return null;
        var e = Math.max(0.5, Math.min(6, PAPER_Y / y));
        return [g[0] * e, g[1] * e, g[2] * e];
    }
    function corrected(raw) {
        if (!gains) return raw.slice();
        return raw.map(function (v, i) { return Math.max(0, Math.min(255, Math.round(CMX.l2s(Math.min(1, CMX.s2l(v / 255) * gains[i])) * 255))); });
    }
    function showLoupe(pt, cx, cy) {
        if (!loupe || !lctx) return;
        var size = lcv.width, half = Math.floor(LOUPE_WIN / 2);
        lctx.imageSmoothingEnabled = false;
        lctx.fillStyle = '#000'; lctx.fillRect(0, 0, size, size);
        lctx.drawImage(src, pt.x - half, pt.y - half, LOUPE_WIN, LOUPE_WIN, 0, 0, size, size);
        var cell = size / LOUPE_WIN, win = Math.min(photo.area, LOUPE_WIN) * cell;
        lctx.strokeStyle = 'rgba(255,255,255,.95)'; lctx.lineWidth = 1.5;
        lctx.strokeRect((size - win) / 2, (size - win) / 2, win, win);
        lctx.strokeStyle = 'rgba(0,0,0,.85)'; lctx.lineWidth = 1;
        lctx.beginPath(); lctx.moveTo(size / 2, size / 2 - 8); lctx.lineTo(size / 2, size / 2 + 8); lctx.moveTo(size / 2 - 8, size / 2); lctx.lineTo(size / 2 + 8, size / 2); lctx.stroke();
        var hx = $('cmr-loupe-hex'); if (hx) hx.textContent = hexOf(corrected(sampleAt(pt.x, pt.y)));
        var stage = view.parentNode.getBoundingClientRect();
        var lw = loupe.offsetWidth || 132, lh = loupe.offsetHeight || 150;
        var px = cx - stage.left - lw / 2, py = cy - stage.top - lh - 18;
        if (py < -8) py = cy - stage.top + 22;
        px = Math.max(-6, Math.min(stage.width - lw + 6, px));
        loupe.style.left = px + 'px'; loupe.style.top = py + 'px';
        loupe.classList.add('is-on');
    }
    function hideLoupe() { if (loupe) loupe.classList.remove('is-on'); }
    function commit(pt) {
        lastPt = pt;
        var raw = sampleAt(pt.x, pt.y);
        if (photo.wbMode) {
            ensurePickCore().then(function () {
                var g = makeGains(raw);
                if (!g) { toast(t('ph_wb_refused', 'Not white'), 'warning'); ae('wb_refused', {}); return; }
                gains = g;
                picks = picks.filter(function (p) { return p.kind !== 'white'; });
                picks.push({ x: pt.x, y: pt.y, kind: 'white', raw: raw });
                setWbMode(false); renderWb(); drawView();
                // re-read every colour already taken from this photo
                if (target && target.src === 'photo' && target.raw) setTarget(corrected(target.raw), 'photo', null, target.raw);
                if (obs && obs.src === 'photo' && obs.raw) setObserved(corrected(obs.raw), 'photo', obs.raw);
                ae('wb_set', {});
            }, function () { setWbMode(false); ae('wb_core_failed', {}); });
            return;
        }
        var rgb = corrected(raw);
        picks = picks.filter(function (p) { return p.kind !== photo.purpose || p.kind === 'paint'; });
        picks.push({ x: pt.x, y: pt.y, kind: photo.purpose, raw: raw });
        drawView();
        ae('photo_pick', { p: photo.purpose, wb: gains ? 1 : 0, a: photo.area });
        if (photo.purpose === 'paint') {
            var c = addCustom(hexOf(rgb), '', 'normal', 'photo');
            toast(fmt(t('ph_paint_added', ':name'), { name: c.name }));
        } else if (photo.purpose === 'mix') {
            openCorrection();
            setObserved(rgb, 'photo', raw);
            toast(t('ph_mix_taken', ''), 'info');
        } else {
            setTarget(rgb, 'photo', null, raw);
        }
    }
    if (view) {
        var dragging = false;
        view.addEventListener('pointerdown', function (e) {
            if (!photo.has) return;
            dragging = true;
            try { view.setPointerCapture(e.pointerId); } catch (x) {}
            showLoupe(toSource(e.clientX, e.clientY), e.clientX, e.clientY);
        });
        view.addEventListener('pointermove', function (e) {
            if (!photo.has) return;
            if (!dragging && e.pointerType === 'touch') return;
            showLoupe(toSource(e.clientX, e.clientY), e.clientX, e.clientY);
        });
        view.addEventListener('pointerup', function (e) {
            if (!photo.has || !dragging) return;
            dragging = false;
            try { view.releasePointerCapture(e.pointerId); } catch (x) {}
            if (e.pointerType === 'touch') hideLoupe();
            commit(toSource(e.clientX, e.clientY));
        });
        view.addEventListener('pointercancel', function () { dragging = false; hideLoupe(); });
        view.addEventListener('pointerleave', function () { if (!dragging) hideLoupe(); });
        view.addEventListener('keydown', function (e) {
            if (!photo.has) return;
            var p = lastPt || { x: Math.round(src.width / 2), y: Math.round(src.height / 2) };
            var step = e.shiftKey ? 10 : 1, nx = p.x, ny = p.y, moved = true;
            if (e.key === 'ArrowLeft') nx -= step; else if (e.key === 'ArrowRight') nx += step;
            else if (e.key === 'ArrowUp') ny -= step; else if (e.key === 'ArrowDown') ny += step;
            else moved = false;
            if (moved) {
                e.preventDefault();
                lastPt = { x: Math.max(0, Math.min(src.width - 1, nx)), y: Math.max(0, Math.min(src.height - 1, ny)) };
                var r = view.getBoundingClientRect();
                showLoupe(lastPt, r.left + lastPt.x * r.width / src.width, r.top + lastPt.y * r.height / src.height);
                return;
            }
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hideLoupe(); commit(p); }
        });
        var rt = null;
        W.addEventListener('resize', function () { if (!photo.has) return; clearTimeout(rt); rt = setTimeout(drawView, 120); });
    }
    var drop = $('cmr-drop');
    if (drop) {
        if (typeof W.__bindUploaderFile === 'function') W.__bindUploaderFile(drop, loadImageFile);
        else drop.addEventListener('tool-uploader:file', function (e) { if (e.detail && e.detail.file) loadImageFile(e.detail.file); });
        drop.addEventListener('tool-uploader:clear', function () {
            photo.has = false; gains = null; picks = [];
            var box = $('cmr-photo'); if (box) box.hidden = true;
        });
    }
    var cam = $('cmr-camera-input');
    if (cam) cam.addEventListener('change', function () { if (cam.files && cam.files[0]) { ae('camera_used', {}); loadImageFile(cam.files[0]); } cam.value = ''; });
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        navigator.mediaDevices.enumerateDevices().then(function (list) {
            if (list.some(function (d) { return d.kind === 'videoinput'; })) { var pi = $('cmr-photo-in'); if (pi) pi.classList.add('cmr-has-camera'); }
        }).catch(function () {});
    }
    document.addEventListener('paste', function (e) {
        if (root.hidden) return;
        var tag = (e.target && e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || (e.target && e.target.isContentEditable)) return;
        var items = (e.clipboardData && e.clipboardData.items) || [];
        for (var i = 0; i < items.length; i++) {
            if (items[i].type && items[i].type.indexOf('image/') === 0) {
                var f = items[i].getAsFile();
                if (f) { e.preventDefault(); ae('photo_paste', {}); loadImageFile(f); return; }
            }
        }
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-purpose]'), function (b) {
        b.addEventListener('click', function () { setPurpose(b.getAttribute('data-purpose')); });
    });
    function setPurpose(p) {
        photo.purpose = p;
        Array.prototype.forEach.call(root.querySelectorAll('[data-purpose]'), function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-purpose') === p ? 'true' : 'false'); });
        if (photo.wbMode) setWbMode(false);
    }
    Array.prototype.forEach.call(root.querySelectorAll('[data-area]'), function (b) {
        b.title = fmt(t('ph_area_title', ''), { n: b.getAttribute('data-area') });
        b.addEventListener('click', function () {
            photo.area = +b.getAttribute('data-area') || 11;
            Array.prototype.forEach.call(root.querySelectorAll('[data-area]'), function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        });
    });
    var wbBtn = $('cmr-wb'), wbReset = $('cmr-wb-reset');
    function setWbMode(on) { photo.wbMode = !!on; renderWb(); }
    function renderWb() {
        var st = $('cmr-wb-state'); if (!st) return;
        st.className = 'cmr-wb-state' + (photo.wbMode ? ' is-wait' : gains ? ' is-on' : '');
        st.textContent = photo.wbMode ? t('ph_wb_waiting', '') : gains ? t('ph_wb_on', '') : '';
        if (wbReset) wbReset.hidden = !gains;
        if (wbBtn) wbBtn.setAttribute('aria-pressed', photo.wbMode ? 'true' : 'false');
    }
    if (wbBtn) wbBtn.addEventListener('click', function () { setWbMode(!photo.wbMode); if (photo.wbMode) ensurePickCore().catch(function () {}); });
    if (wbReset) wbReset.addEventListener('click', function () {
        gains = null; picks = picks.filter(function (p) { return p.kind !== 'white'; }); renderWb(); drawView();
        if (target && target.src === 'photo' && target.raw) setTarget(target.raw, 'photo', null, target.raw);
    });

    /* ============================================================= solver ==== */
    /* The worker boots on the first visit of this tab (two script fetches —
       seconds on a slow phone network), so a small search that arrives before
       it is ready runs here instead: 12 paints × 3 is ~40 ms on a desktop.
       Only big ones (the whole library, 4 paints) wait for the worker. */
    var worker = null, workerDead = false, workerReady = false, reqSeq = 0, pending = {};
    function getWorker() {
        if (worker || workerDead || !CFG.worker || typeof Worker !== 'function') return worker;
        try {
            worker = new Worker(CFG.worker);
            worker.onmessage = function (e) {
                var m = e.data || {};
                if (m.ready) { workerReady = true; return; }
                var p = pending[m.id];
                if (!p) return;
                delete pending[m.id];
                if (m.error) p.reject(new Error(m.error)); else p.resolve(m.result);
            };
            worker.onerror = function () {
                workerDead = true; worker = null;
                var ks = Object.keys(pending);
                ks.forEach(function (k) { var p = pending[k]; delete pending[k]; p.fallback(); });
                ae('solver_worker_failed', {});
            };
        } catch (e) { workerDead = true; worker = null; }
        return worker;
    }
    function packPaints(list) {
        return list.map(function (d) { return { R: Array.prototype.slice.call(d.paint.R), T: d.paint.T }; });
    }
    function local(cmd, msg) {
        var ps = msg.paints.map(function (p) { return P.prepare(p.R, p.T); });
        if (cmd === 'solve') return P.solve(msg.target, ps, msg.opts);
        if (cmd === 'suggest') {
            var cands = msg.cands.map(function (p) { return P.prepare(p.R, p.T); });
            return P.suggest(msg.target, ps, cands, msg.opts).map(function (s) { return { cand: cands.indexOf(s.cand), rec: s.rec }; });
        }
        return null;
    }
    function call(cmd, msg) {
        return new Promise(function (resolve, reject) {
            var runLocal = function () { setTimeout(function () { try { resolve(local(cmd, msg)); } catch (e) { reject(e); } }, 0); };
            var w = getWorker();
            var small = cmd === 'solve' && msg.paints.length <= 16 && (msg.opts.maxPaints || 3) <= 3;
            if (!w || (!workerReady && small)) { runLocal(); return; }
            var id = ++reqSeq;
            pending[id] = { resolve: resolve, reject: reject, fallback: runLocal };
            try { w.postMessage(Object.assign({ id: id, cmd: cmd }, msg)); } catch (e) { delete pending[id]; runLocal(); }
        });
    }

    /* ============================================================= recipe ==== */
    var result = null, shownK = null, correction = null, obs = null, solveSeq = 0, schedT = null;
    function schedule(delay) {
        clearTimeout(schedT);
        schedT = setTimeout(solveNow, delay == null ? 120 : delay);
    }
    function solveNow() {
        var box = $('cmr-result'); if (!box) return;
        var paints = activePaints();
        if (!target) { box.innerHTML = ''; box.appendChild(el('p', { class: 'cmr-empty', text: t('rcp_empty', '') })); hideCorrection(); return; }
        if (!paints.length) { box.innerHTML = ''; box.appendChild(el('p', { class: 'cmr-empty', text: t('rcp_need_paints', '') })); hideCorrection(); return; }
        var my = ++solveSeq, t0 = performance.now();
        var slow = setTimeout(function () { if (my === solveSeq) { box.setAttribute('aria-busy', 'true'); var s = box.querySelector('.cmr-busy'); if (!s) box.insertBefore(el('p', { class: 'cmr-note cmr-busy', text: t('rcp_computing', '…') }), box.firstChild); } }, 160);
        call('solve', { target: target.lab, paints: packPaints(paints), opts: { maxPaints: pal.max } }).then(function (res) {
            clearTimeout(slow);
            if (my !== solveSeq) return;
            box.removeAttribute('aria-busy');
            result = { res: res, paints: paints, target: target, ms: Math.round(performance.now() - t0) };
            var rec = P.recommend(res.byCount);
            shownK = rec ? rec.idx.length : null;
            renderResult();
            renderTargetInfo();
            var best = rec ? rec.dE : null;
            ae('recipe', { k: shownK, p: paints.length, max: pal.max, de: best != null ? Math.round(best * 10) / 10 : null, ms: result.ms, cu: paints.filter(function (d) { return d.kind === 'custom'; }).length, src: target.src });
            if (W.__cmxDone) W.__cmxDone('recipe');
            if (best != null && best > 5) suggestPaints();
        }, function () { clearTimeout(slow); box.removeAttribute('aria-busy'); });
    }

    /* The recipe on screen: optimal volumes → whole parts when they fit, the
       result mixed from what is SHOWN (parts), ΔE of that. */
    function current() {
        if (!result) return null;
        if (result.fixed) return fixedCurrent();
        if (!shownK) return null;
        var rec = result.res.byCount[shownK]; if (!rec) return null;
        var ps = result.paints.map(function (d) { return d.paint; });
        var parts = P.toParts(ps, rec, result.target.lab, { maxTotal: 40 });
        var useParts = parts && parts.dE <= Math.max(rec.dE + 0.6, rec.dE * 1.25);
        var vols = useParts ? parts.parts : rec.vols;
        var items = rec.idx.map(function (i, j) { return { desc: result.paints[i], i: i, vol: vols[j], frac: 0 }; });
        var total = items.reduce(function (s, x) { return s + x.vol; }, 0);
        items.forEach(function (x) { x.frac = x.vol / total; });
        items.sort(function (a, b) { return b.vol - a.vol; });
        var sub = items.map(function (x) { return x.desc.paint; });
        var rgb = P.mixRgb(sub, items.map(function (x) { return x.vol; }));
        var dE = P.deltaE2000(result.target.lab, P.labOfRgb(rgb));
        return { items: items, useParts: !!useParts, total: total, rgb: rgb, dE: dE, rec: rec };
    }
    /* After «Done — update the recipe»: the batch the person actually holds
       (recipe + additions, in the same parts), predicted with the strengths
       and offset the miss revealed — not with the model that missed. */
    function fixedCurrent() {
        var f = result.fixed;
        var items = f.idx.map(function (i, j) { return { desc: result.paints[i], i: i, vol: f.vols[j], frac: 0 }; });
        var total = items.reduce(function (s, x) { return s + x.vol; }, 0);
        items.forEach(function (x) { x.frac = x.vol / total; });
        items.sort(function (a, b) { return b.vol - a.vol; });
        var sub = items.map(function (x) { var s = f.s[x.i]; return s ? P.adjust(x.desc.paint, s) : x.desc.paint; });
        var lab = P.mixLab(sub, items.map(function (x) { return x.vol; }));
        lab = [lab[0] + f.offset[0], lab[1] + f.offset[1], lab[2] + f.offset[2]];
        return { items: items, useParts: true, fixed: true, total: total, rgb: P.rgbOfLab(lab), dE: P.deltaE2000(result.target.lab, lab) };
    }
    function amountText(frac) {
        var q = pal.amount * frac, unit = pal.unit === 'g' ? t('rcp_unit_g', 'g') : t('rcp_unit_ml', 'ml');
        var main = q >= 10 ? num(q, 0) : q >= 1 ? num(q, 1) : num(q, 2);
        var s = main + ' ' + unit;
        if (pal.unit === 'ml' && q < 2.5) {
            var drops = Math.max(1, Math.round(q * 20)); // ≈ 20 drops per ml
            s += ' · ' + plural('rcp_drops', drops, 0);
        }
        return s;
    }
    function verdictText(dE) { var v = L.rcp_verdict || {}; var k = P.verdictKey(dE); return { key: k, text: v[k] || k }; }

    function renderResult() {
        var box = $('cmr-result'); if (!box) return;
        box.innerHTML = '';
        var cur = current();
        if (!cur) { box.appendChild(el('p', { class: 'cmr-empty', text: t('rcp_empty', '') })); return; }
        var thx = hexOf(result.target.rgb), phx = hexOf(cur.rgb);
        var lightT = P.labOfRgb(result.target.rgb)[0] > 60, lightP = P.labOfRgb(cur.rgb)[0] > 60;
        box.appendChild(el('div', { class: 'cmr-cmp' }, [
            el('div', { style: 'background:' + thx + ';color:' + (lightT ? '#111' : '#fff') }, [t('rcp_need', 'Target'), el('code', { text: thx })]),
            el('div', { style: 'background:' + phx + ';color:' + (lightP ? '#111' : '#fff') }, [t('rcp_get', 'You get'), el('code', { text: phx })])
        ]));
        var v = verdictText(cur.dE);
        box.appendChild(el('div', { class: 'cmr-verdict' }, [
            el('b', { class: 'cmr-v-' + v.key, text: v.text }),
            el('span', { class: 'cmr-de', title: t('rcp_de_title', ''), text: 'ΔE ' + num(cur.dE, 1) })
        ]));
        // paint-count tabs
        var ks = P.usefulCounts(result.res.byCount), rec = P.recommend(result.res.byCount);
        if (cur.fixed) box.appendChild(el('p', { class: 'cmr-note', text: t('cor_applied', '') }));
        else if (ks.length > 1) {
            var tabs = el('div', { class: 'cmr-counts', role: 'group' });
            ks.forEach(function (k) {
                var r = result.res.byCount[k];
                tabs.appendChild(el('button', {
                    type: 'button', class: 'cmr-chip', 'aria-pressed': k === shownK ? 'true' : 'false',
                    onclick: function () { shownK = k; renderResult(); ae('recipe_count', { k: k }); }
                }, [plural('rcp_count', k, 0) + ' ', el('small', { text: '· ΔE ' + num(r.dE, 1) + (rec && rec.idx.length === k ? ' · ' + t('rcp_recommended', '') : '') })]));
            });
            box.appendChild(tabs);
        }
        // the recipe
        var list = el('div', { class: 'cmr-rec' });
        cur.items.forEach(function (x) {
            var q = cur.useParts ? plural('rcp_parts', x.vol, cur.fixed ? 1 : 0) : num(x.frac * 100, x.frac < 0.1 ? 1 : 0) + '%';
            var sub = cur.useParts ? num(x.frac * 100, x.frac < 0.1 ? 1 : 0) + '% · ' + amountText(x.frac) : amountText(x.frac);
            list.appendChild(el('div', { class: 'cmr-ri' }, [
                swatchOf(x.desc),
                el('span', { class: 'cmr-ri-name' }, [x.desc.name, el('small', { text: captionOf(x.desc) })]),
                el('span', { class: 'cmr-ri-q' }, [q, el('small', { text: sub })])
            ]));
        });
        box.appendChild(list);
        // amount
        var amt = el('input', { type: 'number', min: '1', max: '100000', step: '1', value: pal.amount, 'aria-label': t('rcp_amount', '') });
        var unit = el('select', { 'aria-label': t('rcp_amount', '') }, [el('option', { value: 'ml', text: t('rcp_unit_ml', 'ml') }), el('option', { value: 'g', text: t('rcp_unit_g', 'g') })]);
        unit.value = pal.unit;
        amt.addEventListener('change', function () { var v2 = parseFloat(amt.value); if (v2 > 0) { pal.amount = v2; savePal(); renderResult(); ae('amount', { v: v2, u: pal.unit }); } });
        unit.addEventListener('change', function () { pal.unit = unit.value; savePal(); renderResult(); });
        box.appendChild(el('div', { class: 'cmr-amount' }, [el('span', { text: t('rcp_amount', '') }), amt, unit]));
        box.appendChild(el('p', { class: 'cmr-note', text: cur.useParts ? t('rcp_units_note', '') : t('rcp_no_parts', '') }));
        if (cur.items.some(function (x) { return x.desc.kind === 'custom' && !x.desc.learned; })) box.appendChild(el('p', { class: 'cmr-note is-warn', text: t('rcp_custom_warn', '') }));
        if (cur.items.some(function (x) { return x.desc.learned; })) box.appendChild(el('p', { class: 'cmr-note', text: t('rcp_learned_note', '') }));
        if (cur.items.some(function (x) { return x.desc.kind === 'brand' && x.desc.approx && !x.desc.learned; })) box.appendChild(el('p', { class: 'cmr-note', text: t('rcp_brand_approx_note', '') }));
        box.appendChild(el('p', { class: 'cmr-note', text: t('rcp_accuracy', '') }));
        // actions
        box.appendChild(el('div', { class: 'cmr-actions' }, [
            el('button', { type: 'button', class: 'cmx-apply', text: '🧪 ' + t('rcp_btn_check', 'Check my mix'), onclick: function () { openCorrection(true); ae('correct_open', {}); } }),
            el('button', { type: 'button', class: 'cmx-btn', text: '🎨 ' + t('rcp_btn_mixer', 'Open in mixer'), onclick: toMixer }),
            el('button', { type: 'button', class: 'cmx-btn', text: '🔗 ' + t('rcp_btn_share', 'Share'), onclick: shareRecipe }),
            el('button', { type: 'button', class: 'cmx-btn', text: '🖼️ ' + t('rcp_btn_png', 'Image'), onclick: recipePng })
        ]));
        var sg = el('div', { id: 'cmr-suggest' });
        box.appendChild(sg);
        if (lastSuggest && lastSuggest.forSeq === solveSeq) renderSuggest(lastSuggest.list);
        if (correction) renderCorrection();
    }

    /* ---- «your paints cannot reach it» → which tube would ---------------- */
    var lastSuggest = null;
    /* Who may be suggested. Someone who picked real tubes shops in THOSE
       ranges: «add «Изумрудная» №720 from your Мастер-Класс» is an answer they
       can buy, a library pigment name is homework. Everyone else gets the
       library pigments they have not ticked. A range holds up to ~100 colours,
       so only the 40 whose own tints come nearest the target are tried. */
    function suggestCandidates() {
        var lines = {};
        pal.brand.forEach(function (b) { if (b.on !== false) lines[b.l] = true; });
        if (BRAND_IDX && Object.keys(lines).length) {
            var out = [];
            Object.keys(lines).forEach(function (l) {
                var x = BRAND_IDX[l]; if (!x) return;
                x.L.colors.forEach(function (c) {
                    if (pal.brand.some(function (y) { return y.l === l && y.c === c[0]; })) return;
                    var b = { l: l, c: c[0] }, p = prepBrand(b);
                    if (p) out.push({ kind: 'brand', brand: b, name: tubeName(c), paint: p });
                });
            });
            if (out.length > 40 && result && result.target) {
                var tl = result.target.lab;
                out.forEach(function (d) {
                    var m = Infinity;
                    [0, 0.5, 0.8, 0.95].forEach(function (w) { var e = P.deltaE2000(tl, P.labOfRgb(P.tintRgb(d.paint, WHITE, 1 - w))); if (e < m) m = e; });
                    d._near = m;
                });
                out.sort(function (a, b) { return a._near - b._near; });
                out = out.slice(0, 40);
            }
            return out;
        }
        return P.libraryIds().filter(function (id) { return !pal.on[id]; }).map(function (id) { return { kind: 'lib', id: id, name: pigName(id), paint: P.pigment(id) }; });
    }
    function suggestPaints() {
        var my = solveSeq;
        var paints = result.paints;
        var cands = suggestCandidates();
        if (!cands.length) return;
        var best = P.recommend(result.res.byCount).dE;
        call('suggest', {
            target: result.target.lab, paints: packPaints(paints),
            cands: packPaints(cands),
            opts: { maxPaints: Math.min(3, pal.max) }
        }).then(function (list) {
            if (my !== solveSeq) return;
            var good = list.filter(function (s) { return s.rec.dE < best - 1.5; }).slice(0, 3).map(function (s) { return { desc: cands[s.cand], dE: s.rec.dE }; });
            lastSuggest = { forSeq: my, list: good };
            renderSuggest(good);
            if (good.length) ae('suggest_shown', { n: good.length, de: Math.round(best * 10) / 10, brand: good[0].desc.kind === 'brand' ? 1 : 0 });
        }, function () {});
    }
    function renderSuggest(list) {
        var box = $('cmr-suggest'); if (!box) return;
        box.innerHTML = '';
        if (!list || !list.length) return;
        var wrap = el('div', { class: 'cmr-suggest' }, [el('h3', { text: t('rcp_unreach_title', '') }), el('p', { text: t('rcp_unreach_hint', '') })]);
        list.forEach(function (s) {
            var d = s.desc;
            var label = d.name + (d.kind === 'brand' ? ' (' + captionOf(d) + ')' : '');
            wrap.appendChild(el('div', { class: 'cmr-sg' }, [
                swatchOf(d),
                el('span', { text: label + ' — ' + fmt(t('rcp_suggest_line', 'ΔE :de'), { de: num(s.dE, 1) }) }),
                el('button', {
                    type: 'button', class: 'cmx-btn', text: '＋ ' + t('rcp_suggest_add', 'Add'), onclick: function () {
                        if (d.kind === 'brand') { addBrand(d.brand); ae('suggest_add', { line: d.brand.l }); return; }
                        pal.on[d.id] = true; shown[d.id] = true; pal.set = 'mine'; syncSet(); savePal(); renderPaints(); renderLib(); schedule(0); ae('suggest_add', { id: d.id });
                    }
                })
            ]));
        });
        box.appendChild(wrap);
    }

    /* ---- hand-offs --------------------------------------------------------- */
    function toMixer() {
        var cur = current(); if (!cur) return;
        ae('recipe_to_mixer', {});
        if (typeof W.__cmxLoadMix === 'function') {
            W.__cmxLoadMix(cur.items.map(function (x) {
                var wgt = Math.round(x.vol * 1000) / 1000;
                if (x.desc.kind === 'lib') return { pig: x.desc.id, weight: wgt };
                if (x.desc.kind === 'brand') {
                    var e = brandEntry(x.desc.brand);
                    if (e && e.c[2].length === 1 && e.c[3] === 1) return { pig: e.c[2][0][0], weight: wgt };
                    return { hex: hexOf(P.paintRgb(x.desc.paint)), weight: wgt };
                }
                return { hex: x.desc.custom.hex, weight: wgt };
            }));
        }
    }
    function recipeHash() {
        var ids = P.libraryIds().filter(function (id) { return pal.on[id]; }).join('.');
        var cu = pal.custom.filter(function (c) { return c.on !== false; }).map(function (c) { return c.hex.slice(1) + '~' + c.strength.charAt(0); }).join('.');
        var br = pal.brand.filter(function (b) { return b.on !== false; }).map(function (b) { return b.l + '~' + b.c; }).join('.');
        return '#t=recipe' + (target ? '&tg=' + hexOf(target.rgb).slice(1) : '') + '&n=' + pal.max + (ids ? '&p=' + ids : '') + (cu ? '&cu=' + cu : '') + (br ? '&b=' + br : '');
    }
    function writeHash() {
        if (W.__cmxTab !== 'recipe') return;
        var h = '#t=recipe' + (target ? '&tg=' + hexOf(target.rgb).slice(1) : '');
        if (location.hash !== h) { try { history.replaceState(null, '', h); } catch (e) {} }
    }
    function shareRecipe() {
        var url = location.href.split('#')[0] + recipeHash();
        ae('recipe_share', {});
        if (navigator.share) { navigator.share({ title: document.title, url: url }).catch(function () {}); }
        else { try { navigator.clipboard.writeText(url).then(function () { toast(t('rcp_link_copied', 'Copied')); }, function () {}); } catch (e) {} }
    }
    function recipePng() {
        var cur = current(); if (!cur) return;
        ae('recipe_png', {});
        var Wd = 900, rows = cur.items.length, H = 300 + rows * 76 + 70;
        var cv = document.createElement('canvas'); cv.width = Wd; cv.height = H;
        var x = cv.getContext('2d');
        x.fillStyle = '#ffffff'; x.fillRect(0, 0, Wd, H);
        x.fillStyle = '#111827'; x.font = '700 34px system-ui,Segoe UI,sans-serif'; x.textBaseline = 'top';
        x.fillText(t('rcp_png_title', 'Paint recipe'), 40, 32);
        var thx = hexOf(result.target.rgb), phx = hexOf(cur.rgb);
        x.fillStyle = thx; x.fillRect(40, 92, 400, 150);
        x.fillStyle = phx; x.fillRect(460, 92, 400, 150);
        x.font = '600 18px system-ui,sans-serif';
        x.fillStyle = P.labOfRgb(result.target.rgb)[0] > 60 ? '#111' : '#fff'; x.fillText(t('rcp_need', 'Target') + '  ' + thx, 56, 206);
        x.fillStyle = P.labOfRgb(cur.rgb)[0] > 60 ? '#111' : '#fff'; x.fillText(t('rcp_get', 'You get') + '  ' + phx + '  ·  ΔE ' + num(cur.dE, 1), 476, 206);
        var y = 270;
        cur.items.forEach(function (it) {
            var d = it.desc, mass, tint;
            if (d.kind === 'lib' || d.kind === 'brand') { var p = d.paint; mass = hexOf(P.paintRgb(p)); tint = hexOf(P.tintRgb(p, WHITE, 0.2)); }
            else { mass = d.custom.hex; tint = mass; }
            x.fillStyle = mass; x.fillRect(40, y, 28, 56); x.fillStyle = tint; x.fillRect(68, y, 28, 56);
            x.fillStyle = '#111827'; x.font = '600 24px system-ui,sans-serif'; x.fillText(d.name, 116, y + 4);
            x.fillStyle = '#6b7280'; x.font = '18px system-ui,sans-serif'; x.fillText(captionOf(d), 116, y + 34);
            x.textAlign = 'right'; x.fillStyle = '#111827'; x.font = '700 24px system-ui,sans-serif';
            x.fillText(cur.useParts ? plural('rcp_parts', it.vol, cur.fixed ? 1 : 0) : num(it.frac * 100, 1) + '%', Wd - 40, y + 4);
            x.fillStyle = '#6b7280'; x.font = '18px system-ui,sans-serif';
            x.fillText(num(it.frac * 100, it.frac < 0.1 ? 1 : 0) + '% · ' + amountText(it.frac), Wd - 40, y + 34);
            x.textAlign = 'left';
            y += 76;
        });
        x.fillStyle = '#9ca3af'; x.font = '16px system-ui,sans-serif';
        x.fillText(location.host + location.pathname, 40, H - 40);
        cv.toBlob(function (b) { if (b && W.__downloadFile) W.__downloadFile(b, 'paint-recipe-' + thx.slice(1) + '.png'); });
        if (W.__cmxDone) W.__cmxDone('recipe_png');
    }

    /* ========================================================== correction ==== */
    function hideCorrection() { var b = $('cmr-correct-block'); if (b) b.hidden = true; correction = null; }
    function openCorrection(focus) {
        var b = $('cmr-correct-block'); if (!b) return;
        if (!current()) { toast(t('cor_need_recipe', ''), 'info'); return; }
        b.hidden = false;
        if (focus) { b.scrollIntoView({ block: 'nearest' }); var i = $('cmr-obs-in'); if (i) i.focus({ preventScroll: true }); }
    }
    function setObserved(rgb, srcKind, raw) {
        obs = { rgb: rgb, src: srcKind, raw: raw || null };
        var hx = hexOf(rgb), sw = $('cmr-obs-sw'), pk = $('cmr-obs-pick'), inp = $('cmr-obs-in');
        if (sw) sw.style.background = hx; if (pk) pk.value = hx; if (inp) inp.value = hx;
        runCorrection();
    }
    function runCorrection() {
        var cur = current(); if (!cur || !obs) return;
        var prev = (result.fixed && result.fixed.s) || {};
        var ps = result.paints.map(function (d, i) { return prev[i] ? P.adjust(d.paint, prev[i]) : d.paint; });
        var rec = { idx: cur.items.map(function (x) { return x.i; }), vols: cur.items.map(function (x) { return x.vol; }) };
        var obsLab = P.labOfRgb(obs.rgb);
        var c = P.correct(result.target.lab, ps, rec, obsLab, { maxAdd: 1.5 });
        // strengths relative to the MODEL, not to the previous round
        c.strengths.forEach(function (x) { x.s = x.s * (prev[x.i] || 1); });
        correction = { c: c, rec: rec, cur: cur };
        renderCorrection();
        ae('correct_run', { before: Math.round(c.before * 10) / 10, after: c.options[0] ? Math.round(c.options[0].dE * 10) / 10 : null, n: c.options[0] ? c.options[0].adds.length : 0 });
    }
    function renderCorrection() {
        var out = $('cmr-correct-out'); if (!out || !correction) return;
        var c = correction.c, cur = correction.cur;
        out.innerHTML = '';
        var v0 = verdictText(c.before);
        out.appendChild(el('p', { class: 'cmr-verdict' }, [fmt(t('cor_now', 'ΔE :de. :verdict'), { de: num(c.before, 1), verdict: v0.text })]));
        if (c.before < 1.2) { out.appendChild(el('p', { class: 'cmr-note', text: t('cor_good', '') })); return; }
        /* What the miss says about the paints — only when strength actually
           EXPLAINS it. A leftover offset of several ΔE means something else went
           wrong (a mis-tap on the photo, another light), and blaming a paint's
           strength then would teach the palette a wrong number. */
        var explained = Math.hypot(c.offset[0], c.offset[1], c.offset[2]) < 4;
        var notes = [];
        c.strengths.forEach(function (s) {
            if (explained && (s.s > 1.4 || s.s < 0.7)) {
                var d = result.paints[s.i];
                notes.push(fmt(t(s.s > 1 ? 'cor_stronger' : 'cor_weaker', ''), { name: d.name, x: num(s.s > 1 ? s.s : 1 / s.s, 1) }));
            }
        });
        notes.forEach(function (n) { out.appendChild(el('p', { class: 'cmr-note', text: n })); });
        var best = c.options[0];
        if (!best) {
            out.appendChild(el('p', { class: 'cmr-note is-warn', text: t('cor_none', '') }));
        } else {
            out.appendChild(el('p', { style: 'margin:8px 0 0;font-weight:700;font-size:13.5px', text: t('cor_add_title', 'Add:') }));
            var list = el('div', { class: 'cmr-cor-adds' });
            var addTotal = 0;
            best.adds.forEach(function (a) {
                var d = result.paints[a.i];
                addTotal += a.a;
                var frac = a.a / cur.total;
                var q = cur.useParts ? '+ ' + plural('rcp_parts', a.a, a.a < 3 ? 1 : 0) : '+ ' + num(frac * 100, 1) + '%';
                list.appendChild(el('div', { class: 'cmr-ri' }, [swatchOf(d), el('span', { class: 'cmr-ri-name', text: d.name }), el('span', { class: 'cmr-ri-q' }, [q, el('small', { text: '+ ' + amountText(frac) })])]));
            });
            out.appendChild(list);
            var v1 = verdictText(best.dE);
            out.appendChild(el('p', { class: 'cmr-note', text: fmt(t('cor_expect', ''), { de: num(best.dE, 1), verdict: v1.text }) }));
            if (addTotal > cur.total * 0.5) out.appendChild(el('p', { class: 'cmr-note is-warn', text: t('cor_big', '') }));
        }
        var strongNotes = explained ? c.strengths.filter(function (s) { return s.s > 1.4 || s.s < 0.7; }) : [];
        var acts = el('div', { class: 'cmr-actions' });
        if (best) acts.appendChild(el('button', { type: 'button', class: 'cmx-apply', text: '✓ ' + t('cor_apply', 'Apply'), onclick: function () { applyCorrection(best); } }));
        if (strongNotes.length) acts.appendChild(el('button', {
            type: 'button', class: 'cmx-btn', text: '💾 ' + t('cor_remember', 'Remember'), onclick: function () {
                strongNotes.forEach(function (s) {
                    var d = result.paints[s.i];
                    var prev = pal.learned[d.key] || 1;
                    pal.learned[d.key] = Math.max(0.2, Math.min(6, Math.round(prev * s.s * 100) / 100));
                });
                savePal(); renderPaints(); toast(t('cor_remembered', '')); ae('correct_remember', { n: strongNotes.length });
                correction = null; var o = $('cmr-correct-out'); if (o) o.innerHTML = '';
                schedule(0);
            }
        }));
        out.appendChild(acts);
    }
    function applyCorrection(opt) {
        var cur = correction.cur, c = correction.c;
        // the batch is now recipe + additions — keep it in the person's parts
        var vols = {};
        cur.items.forEach(function (x) { vols[x.i] = (vols[x.i] || 0) + x.vol; });
        opt.adds.forEach(function (a) { vols[a.i] = (vols[a.i] || 0) + a.a; });
        var idx = Object.keys(vols).map(Number);
        var s = {}; c.strengths.forEach(function (x) { if (Math.abs(x.s - 1) > 1e-6) s[x.i] = x.s; });
        result.fixed = { idx: idx, vols: idx.map(function (i) { return vols[i]; }), s: s, offset: c.offset };
        correction = null; obs = null;
        var o = $('cmr-correct-out'); if (o) o.innerHTML = '';
        renderResult();
        toast(t('cor_applied', ''));
        ae('correct_apply', { n: opt.adds.length });
    }

    /* ============================================================== wiring ==== */
    var tIn = $('cmr-target-in'), tGo = $('cmr-target-go'), tPick = $('cmr-target-pick');
    function goText() {
        parseTarget(tIn ? tIn.value : '').then(function (r) {
            if (!r) { if (tIn && tIn.value.trim()) { tIn.classList.add('is-err'); toast(LN.invalid_color || 'Colour not recognised', 'error'); } return; }
            if (tIn) tIn.classList.remove('is-err');
            setTarget(r.rgb, 'text', r.label);
        });
    }
    if (tGo) tGo.addEventListener('click', goText);
    if (tIn) {
        tIn.addEventListener('keydown', function (e) { if (e.key === 'Enter') goText(); });
        tIn.addEventListener('input', function () { tIn.classList.remove('is-err'); });
    }
    if (tPick) tPick.addEventListener('change', function () { var rgb = rgbOfHex(tPick.value); if (rgb) setTarget(rgb, 'picker'); });

    var oIn = $('cmr-obs-in'), oGo = $('cmr-obs-go'), oPick = $('cmr-obs-pick'), oPhoto = $('cmr-obs-photo');
    function goObs() {
        var c = CMX.parse(oIn ? oIn.value : '');
        if (!c) { if (oIn) oIn.classList.add('is-err'); return; }
        if (oIn) oIn.classList.remove('is-err');
        setObserved(CMX.to255(c), 'text');
    }
    if (oGo) oGo.addEventListener('click', goObs);
    if (oIn) oIn.addEventListener('keydown', function (e) { if (e.key === 'Enter') goObs(); });
    if (oPick) oPick.addEventListener('change', function () { var rgb = rgbOfHex(oPick.value); if (rgb) setObserved(rgb, 'picker'); });
    if (oPhoto) oPhoto.addEventListener('click', function () {
        setPurpose('mix');
        var pin = $('cmr-photo-in'), ph = $('cmr-photo');
        var tgt = (ph && !ph.hidden) ? ph : pin;
        if (tgt) tgt.scrollIntoView({ block: 'nearest' });
    });

    var setSel = $('cmr-set');
    if (setSel) {
        setSel.value = pal.set;
        setSel.addEventListener('change', function () { if (setSel.value !== 'mine') applyPreset(setSel.value); else { pal.set = 'mine'; savePal(); } renderPaints(); renderLib(); schedule(0); ae('palette_set', { s: setSel.value }); });
    }
    var maxSel = $('cmr-max');
    if (maxSel) {
        Array.prototype.forEach.call(maxSel.options, function (o) { o.textContent = plural('rcp_count', +o.value, 0); });
        maxSel.value = String(pal.max);
        maxSel.addEventListener('change', function () { pal.max = +maxSel.value || 3; savePal(); schedule(0); ae('max_paints', { n: pal.max }); });
    }
    var addOpen = $('cmr-add-open'), addBox = $('cmr-add');
    if (addOpen && addBox) addOpen.addEventListener('click', function () {
        addBox.hidden = !addBox.hidden;
        addOpen.setAttribute('aria-expanded', addBox.hidden ? 'false' : 'true');
        if (!addBox.hidden) {
            renderLib();
            var bb = $('cmr-add-brand'); if (bb && !bb.hidden) openBrandPicker();
            addBox.scrollIntoView({ block: 'nearest' });
        }
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-add]'), function (b) {
        b.addEventListener('click', function () {
            var which = b.getAttribute('data-add');
            Array.prototype.forEach.call(root.querySelectorAll('[data-add]'), function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
            $('cmr-add-lib').hidden = which !== 'lib';
            $('cmr-add-color').hidden = which !== 'color';
            $('cmr-add-photo').hidden = which !== 'photo';
            var bb = $('cmr-add-brand'); if (bb) bb.hidden = which !== 'brand';
            if (which === 'photo') setPurpose('paint');
            if (which === 'brand') openBrandPicker();
        });
    });
    /* ---- the brand picker ------------------------------------------------ */
    var brandLine = $('cmr-brand-line'), brandQ = $('cmr-brand-q'), brandList = $('cmr-brand-list');
    function openBrandPicker() {
        if (brandList && !BRAND_IDX) { brandList.innerHTML = ''; brandList.appendChild(el('p', { class: 'cmr-note', text: t('rcp_brand_loading', '…') })); }
        ensureBrands().then(function () { fillBrandLines(); renderBrandList(); }, function () {
            if (brandList) { brandList.innerHTML = ''; brandList.appendChild(el('p', { class: 'cmr-note is-warn', text: t('rcp_brand_failed', '') })); }
        });
        ae('brand_open', {});
    }
    function fillBrandLines() {
        if (!brandLine || !W.CMXBrands || brandLine.getAttribute('data-filled')) return;
        brandLine.setAttribute('data-filled', '1');
        var groups = {};
        W.CMXBrands.lines.forEach(function (L) {
            var g = groups[L.brand];
            if (!g) { g = groups[L.brand] = el('optgroup', { label: brandLabel(L) }); brandLine.appendChild(g); }
            g.appendChild(el('option', { value: L.id, text: lineLabel(L) + ' — ' + t('rcp_medium_' + L.medium, L.medium) + ' (' + L.colors.length + ')' }));
        });
        var last = null;
        try { last = settings && settings.load('color-mixer', 'brandLine', null); } catch (e) {}
        if (!last && pal.brand.length) last = pal.brand[pal.brand.length - 1].l;
        if (last && BRAND_IDX[last]) brandLine.value = last;
    }
    function renderBrandList() {
        if (!brandList) return;
        brandList.innerHTML = '';
        var x = brandLine && BRAND_IDX && BRAND_IDX[brandLine.value];
        if (!x) { brandList.appendChild(el('p', { class: 'cmr-note', text: t('rcp_brand_pick_line', '') })); return; }
        var q = (brandQ && brandQ.value || '').trim().toLowerCase().replace(/ё/g, 'е');
        var shownN = 0;
        x.L.colors.forEach(function (c) {
            var hay = (c[1] + ' ' + (c[7] || '') + ' ' + c[0] + ' ' + (c[6] || '')).toLowerCase().replace(/ё/g, 'е');
            if (q && hay.indexOf(q) < 0) return;
            shownN++;
            var b = { l: x.L.id, c: c[0] };
            var have = pal.brand.some(function (y) { return y.l === b.l && y.c === b.c; });
            brandList.appendChild(el('button', {
                type: 'button', class: 'cmr-paint', 'aria-pressed': have ? 'true' : 'false', title: tubeName(c) + (isApprox(c) ? ' — ' + t('rcp_brand_approx', '≈') : ''),
                onclick: function () { if (have) removeBrand(b); else addBrand(b); }
            }, [swatchOf({ kind: 'brand', brand: b }), el('span', { class: 'cmr-pname' }, [tubeName(c), el('small', { text: '№' + c[0] + (c[6] ? ' · ' + c[6] : '') + (isApprox(c) ? ' · ≈' : '') })]), el('span', { class: 'cmr-pcheck', style: 'visibility:visible', text: have ? '✓' : '＋' })]));
        });
        if (!shownN) brandList.appendChild(el('p', { class: 'cmr-note', text: t('rcp_brand_none', '') }));
    }
    function addBrand(b) {
        if (pal.brand.some(function (y) { return y.l === b.l && y.c === b.c; })) return;
        /* The first real paint switches the library pigments off: a recipe
           «from my paints» must not borrow pigments the person does not own.
           Said out loud, and undone by one tap on «Набор». */
        if (!pal.brand.length && pal.set !== 'mine') {
            pal.on = {}; shown = {};
            toast(t('rcp_brand_lib_off', ''), 'info');
        }
        pal.brand.push({ l: b.l, c: b.c, on: true });
        pal.set = 'mine'; syncSet(); savePal();
        renderPaints(); renderLib(); renderBrandList(); schedule();
        ae('paint_add', { src: 'brand', line: b.l });
    }
    function removeBrand(b) {
        pal.brand = pal.brand.filter(function (y) { return !(y.l === b.l && y.c === b.c); });
        delete pal.learned['b:' + b.l + ':' + b.c];
        savePal(); renderPaints(); renderBrandList(); schedule();
    }
    if (brandLine) brandLine.addEventListener('change', function () {
        try { settings && settings.save('color-mixer', 'brandLine', brandLine.value); } catch (e) {}
        renderBrandList();
        ae('brand_line', { line: brandLine.value });
    });
    if (brandQ) brandQ.addEventListener('input', function () { renderBrandList(); });

    var nPick = $('cmr-new-pick'), nHex = $('cmr-new-hex'), nSw = $('cmr-new-sw'), nName = $('cmr-new-name'), nStr = $('cmr-new-strength'), nAdd = $('cmr-new-add');
    function syncNew(hx) { if (nSw) nSw.style.background = hx; if (nPick) nPick.value = hx; }
    if (nPick) nPick.addEventListener('input', function () { if (nHex) nHex.value = nPick.value; syncNew(nPick.value); });
    if (nHex) nHex.addEventListener('input', function () { var c = CMX.parse(nHex.value); if (c) { nHex.classList.remove('is-err'); syncNew(CMX.hex(c)); } else nHex.classList.add('is-err'); });
    if (nAdd) nAdd.addEventListener('click', function () {
        var c = CMX.parse(nHex ? nHex.value : '');
        if (!c) { if (nHex) nHex.classList.add('is-err'); return; }
        addCustom(CMX.hex(c), nName ? nName.value : '', nStr ? nStr.value : 'normal', 'color');
        if (nName) nName.value = '';
    });
    if (nHex) syncNew(nHex.value);

    /* ============================================================== boot ====== */
    function readHash() {
        var p = {};
        location.hash.replace(/^#/, '').split('&').forEach(function (kv) { if (!kv) return; var a = kv.split('='); p[a[0]] = decodeURIComponent(a[1] || ''); });
        return p;
    }
    function bootFromHash() {
        var p = readHash();
        if (p.t !== 'recipe') return false;
        if (p.p || p.cu || p.b) {
            pal.on = {};
            shown = {};
            (p.p || '').split('.').forEach(function (id) { if (P.LIB[id]) { pal.on[id] = true; shown[id] = true; } });
            if (p.cu) {
                var keep = pal.custom.slice();
                p.cu.split('.').forEach(function (tok) {
                    var s = tok.split('~'), hx = '#' + s[0];
                    if (!/^#[0-9a-f]{6}$/i.test(hx)) return;
                    if (keep.some(function (c) { return c.hex === hx.toLowerCase(); })) { keep.forEach(function (c) { if (c.hex === hx.toLowerCase()) c.on = true; }); return; }
                    customSeq++;
                    pal.custom.push({ uid: 'u' + customSeq, hex: hx.toLowerCase(), name: fmt(t('ph_paint_default', ':n'), { n: customSeq }), strength: ({ w: 'weak', n: 'normal', s: 'strong', v: 'very' })[s[1]] || 'normal', on: true });
                });
            }
            if (p.b) {
                pal.brand = [];
                p.b.split('.').forEach(function (tok) {
                    var sp = tok.split('~');
                    if (sp.length === 2 && /^[a-z0-9-]{3,40}$/.test(sp[0]) && /^[0-9A-Za-z]{1,8}$/.test(sp[1])) pal.brand.push({ l: sp[0], c: sp[1], on: true });
                });
                if (pal.brand.length) ensureBrands().then(function () { renderPaints(); schedule(0); }, function () {});
            }
            pal.set = 'mine';
        }
        if (p.n >= 1 && p.n <= 4) pal.max = +p.n;
        syncSet(); if (maxSel) maxSel.value = String(pal.max);
        if (p.tg && /^[0-9a-f]{6}$/i.test(p.tg)) setTarget(rgbOfHex('#' + p.tg), 'link');
        return true;
    }
    W.__cmrSetTarget = function (rgb, srcKind) { setTarget(rgb, srcKind || 'mixer'); };
    /* A #t=recipe link followed while the page is open (a shared link pasted
       into the address bar, a card link without this script): color-mixer.js
       calls this BEFORE switching tabs — the tab switch runs writeHash(), which
       would replace the new hash with the old target before anyone read it. */
    W.__cmrFromHash = bootFromHash;
    /* «Use my paints» on a «Which paints to mix» card below the tool: the
       card's colour becomes the target, on the person's own palette (an
       untouched palette gives the card's own numbers — same default set,
       same engine). The cards sit a screen or more below the recipe, so the
       result heading is brought into view: the person asked for it with this
       click, and «nearest» does nothing when it is already on screen. */
    document.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('[data-cmr-tg]') : null;
        if (!a) return;
        var rgb = rgbOfHex('#' + a.getAttribute('data-cmr-tg'));
        if (!rgb) return;
        e.preventDefault();
        if (W.__cmxShowTab) W.__cmxShowTab('recipe');
        var card = a.closest('.cmx-pop-card'), nameEl = card && card.querySelector('.cmx-pop-name');
        setTarget(rgb, 'popular', nameEl ? nameEl.textContent.trim() : null);
        var block = $('cmr-result-block'), head = block && block.querySelector('.cmr-h');
        if (head || block) (head || block).scrollIntoView({ block: 'nearest' });
        ae('popular_go', { k: a.getAttribute('data-cmr-pop') || '' });
    });
    W.addEventListener('cmx:tab', function () { writeHash(); if (photo.has) drawView(); getWorker(); });
    if (W.__cmxTab === 'recipe') getWorker();

    renderPaints();
    renderLib();
    if (pal.brand.length) ensureBrands().then(function () { renderPaints(); schedule(0); }, function () {});
    bootFromHash();
    if (!target) {
        var box = $('cmr-result'); if (box) { box.innerHTML = ''; box.appendChild(el('p', { class: 'cmr-empty', text: t('rcp_empty', '') })); }
    }
})();
